import { Component } from '@angular/core';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MapAnimatorService } from '../../services/map-animator.service';
import { Router } from '@angular/router';
import { MapService } from '../../services/map.service';

@Component({
  selector: 'app-export-settings',
  templateUrl: './export-settings.component.html',
  styleUrls: ['./export-settings.component.scss'],
  standalone: false,
})
export class ExportSettingsComponent {
  options: UntypedFormGroup;

  parse_error: boolean = false;

  public route_file: File | undefined;
  public route_uploaded: boolean = false;
  public loading = false;
  public error_message: string = '';
  public has_valid_path: boolean = false;
  public showAdvancedSettings: boolean = false;
  private _suppress_auto_waypoint_trigger: boolean = false;

  constructor(
    private mapAnimator: MapAnimatorService,
    fb: UntypedFormBuilder,
    private router: Router,
    private mapService: MapService,
  ) {
    this.mapAnimator.error$.subscribe((err) => {
      this.error_message = err;
    });

    this.options = fb.group({
      velocity: new UntypedFormControl(this.mapAnimator.velocity$.getValue()),
      map_scaling: new UntypedFormControl(15_000),
      departure_time: new UntypedFormControl(
        new Date().toISOString().substring(0, 16),
      ),
      creator_name: new UntypedFormControl(''),
      legend_position: new UntypedFormControl('lower right'),
      map_layers: new UntypedFormControl('ch.swisstopo.pixelkarte-farbe'),
      auto_scale: new UntypedFormControl(false),
      route_name: new UntypedFormControl(''),
      name_points_in_export: new UntypedFormControl(true),
      number_points_in_export: new UntypedFormControl(false),
      automatic_waypoint_selection: new FormControl<boolean>(
        this.mapAnimator.auto_waypoints,
      ),
    });

    // we need this try-catch block to ensure the loaded data is compatible with the current form layout
    try {
      if (
        localStorage['form_values'] &&
        this.isJsonString(localStorage['form_values'])
      ) {
        this.options.patchValue(JSON.parse(localStorage['form_values']));
        console.log('Loaded form values from local storage');
      }
    } catch (_) {
      // safe to ignore
    }

    // listen to changes of automatic_waypoint_selection
    this.options
      .get('automatic_waypoint_selection')
      ?.valueChanges.subscribe((val) => {
        if (!this._suppress_auto_waypoint_trigger) {
          this.mapAnimator.set_automatic_waypoint_selection(val);
        }
      });

    this.options.get('velocity')?.valueChanges.subscribe((val) => {
      this.mapAnimator.set_velocity(val);
    });

    // enforce that either name_points_in_export or number_points_in_export is true
    this.options.get('name_points_in_export')?.valueChanges.subscribe((val) => {
      if (val) {
        this.options.get('number_points_in_export')?.setValue(false);
      }
    });
    this.options
      .get('number_points_in_export')
      ?.valueChanges.subscribe((val) => {
        if (val) {
          this.options.get('name_points_in_export')?.setValue(false);
        }
      });

    console.log('Form values:', this.options.value);
    this.mapAnimator?.path$.subscribe((path) => {
      this.has_valid_path = path.length !== 0;
      this.route_uploaded = this.has_valid_path;
    });

    this.mapAnimator?.suggestedRouteName$.subscribe((name) => {
      const currentName = this.options.get('route_name')?.value;
      if (!currentName || currentName.trim() === '') {
        this.options.patchValue({ route_name: name });
      }
    });
  }

  ngOnInit() {
    this.mapAnimator?.auto_waypoints_baked$.subscribe(() => {
      this._suppress_auto_waypoint_trigger = true;
      this.options
        .get('automatic_waypoint_selection')
        ?.setValue(false, { emitEvent: false });
      setTimeout(() => {
        this._suppress_auto_waypoint_trigger = false;
      }, 50);
    });
  }

  onUserEdited() {
    this.mapAnimator.set_automatic_waypoint_selection(false);
    this._suppress_auto_waypoint_trigger = true;
    this.options
      .get('automatic_waypoint_selection')
      ?.setValue(false, { emitEvent: false });
    setTimeout(() => {
      this._suppress_auto_waypoint_trigger = false;
    }, 50);
  }

  public new_route_uploaded(route_file: File) {
    this.loading = true;

    this.route_file = route_file;
    this.mapAnimator
      .replace_route(route_file)
      .then((route_name) => {
        this.options.patchValue({ route_name: route_name });
        this.mapService.zoom_to_route_area(this.mapAnimator.path);
        this.route_uploaded = true;
        this.loading = false;
      })
      .catch(() => {
        this.parse_error = true;
        this.loading = false;
      });
  }

  public delete_route_file() {
    this.route_uploaded = false;
    this.mapAnimator.clear();
  }

  public download_map() {
    console.log('Downloading map...');
    const settings = this.options.value;
    this.mapAnimator
      .download_map(settings)
      .then((uuid) => this.router.navigate(['pending', uuid]))
      .catch((err) => (this.error_message = err?.message ?? String(err)));
  }

  public retry_after_error() {
    if (this.mapAnimator.retry_failed_action()) return;
    // the error came from the export itself, the user can simply click export again
    this.error_message = '';
  }

  private isJsonString(str: string): boolean {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  async finish_drawing() {
    this.route_uploaded = true;
    await this.mapAnimator.finish_drawing();
  }

  get magnetic_paths(): boolean {
    return this.mapAnimator.magnetic_paths;
  }

  toggle_magnetic_paths() {
    this.mapAnimator.toggle_magnetic_paths();
  }

  invert_route() {
    this.mapAnimator.invert_route();
  }

  undo() {
    this.mapAnimator.undo();
  }

  redo() {
    this.mapAnimator.redo();
  }

  can_undo(): boolean {
    return this.mapAnimator.can_undo();
  }

  can_redo(): boolean {
    return this.mapAnimator.can_redo();
  }

  cancel_drawing() {
    this.mapAnimator.clear();
  }
}
